<template>
  <div class="modal-overlay">
    <div class="modal-card">

      <!-- Loading -->
      <template v-if="isLoading">
        <ProgressSpinner style="width: 3rem; height: 3rem;" strokeWidth="4" class="mb-4" />
        <h2 class="text-xl font-bold mb-2 text-900">Checking your account...</h2>
        <p class="text-600 text-sm">Please wait while we check for any pending items.</p>
      </template>

      <!-- Fetch error -->
      <template v-else-if="error">
        <div class="icon-box danger-bg border-round-lg flex align-items-center justify-content-center mb-4">
          <i class="pi pi-times-circle text-xl" style="color: #EF4444;"></i>
        </div>
        <h2 class="text-xl font-bold mb-2 text-900">Failed to check account status</h2>
        <p class="text-600 mb-5 text-sm px-2">{{ error }}</p>
        <div class="flex justify-content-center gap-3 w-full flex-wrap">
          <Button label="Try Again" :class="buttonClass" class="w-10rem" @click="$emit('retry')" />
          <Button label="Cancel" :class="buttonClass" class="w-10rem" @click="$emit('cancel')" />
        </div>
      </template>

      <!-- Has pending items -->
      <template v-else-if="hasPending">
        <div class="icon-box warning-bg border-round-lg flex align-items-center justify-content-center mb-4">
          <i class="pi pi-exclamation-triangle text-xl warning-text"></i>
        </div>
        <h2 class="text-xl font-bold mb-2" style="color: #6D4C20;">
          {{ role === 'institute' ? 'Heads up — pending items found' : 'Please resolve pending items first' }}
        </h2>
        <p class="text-600 mb-4 text-sm line-height-3 px-2">{{ roleBlockMessage }}</p>

        <div class="pending-list w-full mb-2">
          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="pending-item flex align-items-start gap-2 p-2 mb-1 border-round"
            :class="i < messages.length - 1 ? 'mb-2' : ''"
          >
            <i class="pi pi-info-circle mt-1 flex-shrink-0" style="color: #B45309; font-size: 0.8rem;"></i>
            <span class="text-xs line-height-3" style="color: #78350F; text-align: left;">{{ msg }}</span>
          </div>
        </div>

        <p v-if="note" class="text-500 text-xs mb-5 px-2 line-height-3 text-center" style="max-width: 32rem;">{{ note }}</p>

        <!-- Institute: non-blocking, can proceed -->
        <div v-if="role === 'institute'" class="flex justify-content-center gap-3 w-full flex-wrap">
          <Button label="Cancel" :class="buttonClass" @click="$emit('cancel')" />
          <Button label="Proceed anyway" :class="buttonClass" @click="$emit('next')" />
        </div>

        <!-- Student / Tutor: blocking -->
        <Button v-else label="Got it, I'll resolve these" :class="buttonClass" @click="$emit('cancel')" />
      </template>

      <!-- All clear — allow proceed -->
      <template v-else>
        <div class="icon-box success-bg border-round-lg flex align-items-center justify-content-center mb-4">
          <div class="border-circle flex align-items-center justify-content-center bg-green-500"
            style="width: 1.5rem; height: 1.5rem;">
            <i class="pi pi-check text-white" style="font-size: 0.8rem; font-weight: bold;"></i>
          </div>
        </div>
        <h2 class="text-xl font-bold mb-2 text-900" style="color: #14532D;">You're all set</h2>
        <p class="text-600 mb-5 text-sm line-height-3 px-2">
          No pending items found. You can proceed with deleting your account.
        </p>
        <div class="flex justify-content-center gap-3 w-full flex-wrap">
          <Button label="Cancel" :class="buttonClass" @click="$emit('cancel')" />
          <Button label="Proceed" :class="buttonClass" @click="$emit('next')" />
        </div>
      </template>

    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';

const props = defineProps({
  role: { type: String, default: 'student' },
  isLoading: { type: Boolean, default: false },
  hasPending: { type: Boolean, default: false },
  messages: { type: Array, default: () => [] },
  note: { type: String, default: null },
  error: { type: String, default: null },
});

defineEmits(['next', 'cancel', 'retry']);

const roleBlockMessage = computed(() => {
  switch (props.role) {
    case 'tutor':
      return 'The following items must be resolved before your account can be deleted. Your enrolled students may be affected, please notify them.';
    case 'institute':
      return 'Please review the following. You can still proceed you\'ll be able to choose what happens to your associated tutors and students in the next step.';
    default:
      return 'The following items must be resolved before your account can be deleted. Please contact your tutors if needed.';
  }
});

const buttonClass = computed(() => {
  switch (props.role) {
    case 'student': return 'orange-button';
    case 'tutor': return 'teal-button';
    case 'institute': return 'yellow-button';
    default: return 'orange-button';
  }
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 24, 39, 0.295);
  padding: 1.5rem;
  backdrop-filter: blur(3px);
}

.modal-card {
  background: white;
  width: 100%;
  max-width: 45rem;
  padding: 2rem 3rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.icon-box {
  width: 3.5rem;
  height: 3.5rem;
  border: 1px solid;
}

.warning-bg {
  background: #FFF9EB;
  border-color: #FDE68A;
}

.warning-text {
  color: #F59E0B;
}

.success-bg {
  background: #F0FDF4;
  border-color: #BBF7D0;
}

.danger-bg {
  background: #FEF2F2;
  border-color: #FECACA;
}

.pending-list {
  max-height: 12rem;
  overflow-y: auto;
}

.pending-item {
  background: #FFFBEB;
  border: 1px solid #FDE68A;
}

@media (max-width: 640px) {
  .modal-overlay {
    padding: 1rem;
  }

  .modal-card {
    padding: 2rem 1.25rem;
  }

  .pending-list {
    max-height: 10rem;
  }
}
</style>
